import api, {
  InviteUserResponse,
  RemoveUserResponse,
} from "../../../../hooks/api-new";
import classNames from "./ManageMembers.module.css";
import { useMemo, useState } from "react";
import { useAxios } from "../../../../hooks/axios";
import { useOrganization } from "../../../context/organization/organization";
import Loader from "../../../common/Loader";
import useUser from "../../../../hooks/user";
import { AxiosError, AxiosResponse } from "axios";
import { toast } from "react-toastify";
import Button from "../../../common/Button";
import { IoMdAdd } from "react-icons/io";
import RadixDialog from "../../../common/dialogs/RadixDialog";
import Search from "../../../common/Search";

function InviteMemberDialog(props: {
  onInvite: (response: AxiosResponse<InviteUserResponse>) => void;
}) {
  const axios = useAxios();
  const inviteUser = api.useInviteUser(axios);

  const [newMemberEmail, setNewMemberEmail] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  return (
    <RadixDialog
      open={isOpen}
      onOpenChange={setIsOpen}
      title={"Invite admin member"}
      trigger={
        <Button>
          <IoMdAdd />
          Invite
        </Button>
      }
      className={classNames.InviteMemberDialog}
      content={
        <div className={classNames.DialogContent}>
          <div className={classNames.Email}>
            <label>Email</label>
            <input
              type="text"
              placeholder="hal@langfinity.ai"
              value={newMemberEmail}
              onChange={(e) => setNewMemberEmail(e.target.value)}
            />
          </div>
          <div className={classNames.Buttons}>
            <Button
              backgroundColor="black"
              color="gray"
              borderColor="dark-gray"
              onClick={() => setIsOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                try {
                  setNewMemberEmail("");
                  const response = await inviteUser({ email: newMemberEmail });
                  props.onInvite(response);
                  setIsOpen(false);
                  toast(
                    <div className="Toast SuccessToast">
                      <div>{"Successfully invited admin member"}</div>
                    </div>,
                    { type: "success" }
                  );
                } catch (error) {
                  if (error instanceof AxiosError) {
                    if (error.status === 403) {
                      toast(
                        <div className="Toast ErrorToast">
                          {"Can't invite any more members"}
                        </div>,
                        {
                          type: "error",
                        }
                      );
                      return;
                    }
                  }
                  throw error;
                }
              }}
            >
              Invite
            </Button>
          </div>
        </div>
      }
    />
  );
}

function RemoveMemberDialog(props: {
  email: string;
  onRemove: (response: AxiosResponse<RemoveUserResponse>) => void;
}) {
  const axios = useAxios();
  const removeUser = api.useRemoveUser(axios);
  const [isOpen, setIsOpen] = useState(false);

  return (
    <RadixDialog
      open={isOpen}
      onOpenChange={setIsOpen}
      title={"Remove admin member"}
      trigger={
        <Button color="gray" backgroundColor="black" borderColor="dark-gray">
          Remove
        </Button>
      }
      className={classNames.InviteMemberDialog}
      content={
        <div className={classNames.DialogContent}>
          <div className={classNames.Email}>
            <label>Email</label>
            <div>{props.email}</div>
          </div>
          <div className={classNames.Buttons}>
            <Button
              color={"gray"}
              borderColor="dark-gray"
              backgroundColor="black"
              onClick={() => setIsOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                try {
                  const response = await removeUser({ email: props.email });
                  props.onRemove(response);
                  setIsOpen(false);
                  toast(
                    <div className="Toast SuccessToast">
                      <div>{"Successfully remvoed admin member"}</div>
                    </div>,
                    { type: "success" }
                  );
                } catch (error) {
                  toast(
                    <div className="Toast ErrorToast">
                      {
                        "Something went wrong, please try again or contact support"
                      }
                    </div>,
                    {
                      type: "error",
                    }
                  );
                  throw error;
                }
              }}
            >
              Remove
            </Button>
          </div>
        </div>
      }
    />
  );
}

export default function ManageMembers() {
  const {
    organization,
    setOrganization,
    pendingInvites,
    setPendingInvites,
    loading,
  } = useOrganization();

  const [user] = useUser();

  const totalMembers = useMemo(() => {
    return (organization?.members.length ?? 0) + (pendingInvites?.length ?? 0);
  }, [organization?.members, pendingInvites]);

  const [searchQuery, setSearchQuery] = useState("");

  if (!organization || !pendingInvites || loading) {
    return <Loader />;
  }

  return (
    <div className={classNames.ManageMembers}>
      <div className={classNames.SearchAndInvite}>
        <Search
          query={searchQuery}
          setQuery={setSearchQuery}
          placeholder="Search for an email address"
        />
        <InviteMemberDialog
          onInvite={(response) => {
            setOrganization((prevState) => ({
              ...prevState!,
              members: response.data.members,
            }));
            setPendingInvites(response.data.pending_invites);
          }}
        />
      </div>
      <div>
        <h2 className={classNames.Title}>
          {"Admin members: "}
          {`(${totalMembers}/${Math.max(
            totalMembers,
            organization.max_members_allowed
          )})`}
        </h2>
        <div className={classNames.Members}>
          {organization.members
            .filter(
              (m) =>
                !searchQuery.length ||
                m.email.toLowerCase().includes(searchQuery.toLowerCase())
            )
            .sort((a, b) => {
              const priorityA = a.isOwner ? 0 : a.id === user.auth?.id ? 1 : 2;
              const priorityB = b.isOwner ? 0 : b.id === user.auth?.id ? 1 : 2;
              return priorityA - priorityB;
            })
            .map((m) => (
              <div key={m.id}>
                <div className={classNames.MemberName}>
                  {m.email}
                  {m.id === user.auth?.id && (
                    <span className={classNames.YouBadge}>You</span>
                  )}
                </div>
                <div className={classNames.InviteStatus}>
                  {m.isOwner ? "Owner" : "Admin"}
                </div>
                <div>
                  {!m.isOwner && m.id !== user.auth?.id ? (
                    <RemoveMemberDialog
                      email={m.email}
                      onRemove={(response) =>
                        setOrganization((prevState) => ({
                          ...prevState!,
                          members: response.data.members,
                        }))
                      }
                    />
                  ) : null}
                </div>
              </div>
            ))}
          {pendingInvites
            .filter(
              (email) =>
                !searchQuery.length ||
                email.toLowerCase().includes(searchQuery.toLowerCase())
            )
            .map((email, index) => (
              <div key={index}>
                <div>{email}</div>
                <div className={classNames.InviteStatus}>Invite pending</div>
                <div>
                  <RemoveMemberDialog
                    email={email}
                    onRemove={(response) =>
                      setPendingInvites(response.data.pending_invites)
                    }
                  />
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
